import { AfterViewChecked, ChangeDetectorRef, Directive, Input, OnDestroy, inject } from '@angular/core';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';
import { HisAuthService } from '../../auth/service/his-auth.service';
import { HisTableContextDirective } from '../../table/directive/his-table-context.directive';
import { HisUserProfileService } from '../service/backend/his-userprofile.service';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-table:not([disableHisTableDirective])',
    standalone: false
})
export class HisTableDirective implements AfterViewChecked, OnDestroy {
    private readonly tableContext = inject( HisTableContextDirective, { optional: true } );
    private userProfileService = inject(HisUserProfileService);
    private cdRef = inject(ChangeDetectorRef);

    private tableComponent = inject(Table, { host: true, optional: true, self: true });
    private paginatorStateInitialized = false;
    private paginatorStateSubscription: Subscription;
    private currentRows: number;
    private hisAuthService = inject( HisAuthService );

    // unused, just kept for template autocomplete assistance
    @Input() disableHisTableDirective: boolean;
    
    ngAfterViewChecked() {
        if ( this.tableContext ) {
            //table managed by HisTableContextDirective; skip directive logic
            return;
        }

        if (this.tableComponent && this.tableComponent.paginator && !this.paginatorStateInitialized) {
            // Prüfen, ob die Tabelle in einem Dialog/Overlay ist (typisch für Multiselect-Popups)
            const isInDialog = this.isTableInDialog();

            if (isInDialog) {
                // Für Tabellen in Dialogs: Directive-Logik überspringen, da die übergeordnete Komponente das Management übernimmt
                this.paginatorStateInitialized = true;
                this.currentRows = this.tableComponent.rows;
                return;
            }

            this.initializePaginatorState();
            if (!this.paginatorStateSubscription) {
                this.paginatorStateSubscription = this.tableComponent.rowsChange.subscribe((rows) => {
                    // Speichere den Zustand nur, wenn sich die Anzahl der Rows tatsächlich ändert und User != anonym ist
                    // Fix für Requests beim Blättern
                    if (rows !== this.currentRows && !this.hisAuthService.isAnonymous) {
                        this.savePaginatorState(rows);
                        this.currentRows = rows;
                    }
                });
            }
        }

        // Fix für Layout Probleme bei Tabellen mit grouped columns
        // TODO: Wird ab PrimeNG v20 nicht mehr benötigt
        if ( this.tableComponent && this.tableComponent.responsive === false ) {
            const element = this.tableComponent.el.nativeElement;
            const firstDiv = element.querySelector( 'div' );
            if ( firstDiv ) {
                firstDiv.classList.remove( 'p-datatable-responsive-stack' );
            }
        }
    }

    private isTableInDialog(): boolean {
        // Prüfe, ob die Tabelle innerhalb eines p-dialog Elements ist
        if (this.tableComponent && this.tableComponent.el && this.tableComponent.el.nativeElement) {
            const element = this.tableComponent.el.nativeElement as HTMLElement;
            const dialogParent = element.closest('p-dialog, .p-dialog');
            return !!dialogParent;
        }
        return false;
    }

    private initializePaginatorState(): void {
        if (this.tableComponent && this.tableComponent.paginator) {
            const key = `table_${this.tableComponent.id}_paginator_rows`;
            const currentTableRows = this.tableComponent.rows;
            console.log('[HisTableDirective] initializePaginatorState(): table id =', this.tableComponent.id, ', key =', key, ', current table.rows =', currentTableRows);

            this.userProfileService.getValueforKey(key).subscribe((savedRows: string) => {
                if (savedRows !== null && savedRows !== undefined) {
                    const rows = parseInt(savedRows, 10);
                    if (!isNaN(rows)) {
                        // Setze eine kleine Verzögerung, um sicherzustellen, dass das Change Detection vollständig abgeschlossen ist
                        // Notwendig, um Zustand korrekt zu initialisieren (andernfalls setzt Paginator wieder auf den Default Wert "5"
                        // beim Blättern zurück)
                        setTimeout(() => {
                            console.log('[HisTableDirective] initializePaginatorState(): SETTING table.rows from', this.tableComponent.rows, 'to', rows);
                            this.tableComponent.rows = rows;
                            this.currentRows = rows;
                            this.cdRef.detectChanges();
                        }, 100);
                    }
                } else {
                    this.currentRows = currentTableRows;
                }
                this.paginatorStateInitialized = true;
            });
        }
    }

    private savePaginatorState(rows: number): void {
        if (this.tableComponent && this.tableComponent.paginator) {
            const key = `table_${this.tableComponent.id}_paginator_rows`;
            this.userProfileService.setNewValue(key, rows.toString(), '5').subscribe(() => { });
        }
    }

    ngOnDestroy() {
        if (this.paginatorStateSubscription) {
            this.paginatorStateSubscription.unsubscribe();
        }
    }
}
